/**
 * Tailwind/CSS-class implementation of the Icon container.
 * Uses the Seeds icon CSS classes from icon.css. Presentational only: it applies
 * the `.Icon`/`.seeds-icon` classes and the `--icon-size` custom property, then
 * renders the precomputed <svg> (passed as children). All name/variant/aria
 * logic lives in iconShared.tsx and IconHybrid.tsx.
 */
import React from "react";
import type { TypeIconSize } from "./IconTypes";

// Utility for merging class names properly.
// Mirror of the local helper in ButtonTailwind.tsx — `cn` is NOT exported from
// @sproutsocial/seeds-react-utilities, so it is duplicated here intentionally.
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

// Pixel lookup driving --icon-size. Mirrors `sizes` in styles.ts (kept local so
// styles.ts stays untouched for the legacy styled-components path).
const sizeMap: { [key in TypeIconSize]: string } = {
  mini: "12px",

  /** TODO: deprecate default in favor of small in future release */
  default: "16px",
  small: "16px",
  medium: "24px",
  large: "32px",
  jumbo: "64px",
};

type TypeIconTailwindProps = {
  size: TypeIconSize;
  fixedWidth: boolean;
  className?: string;
  children: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<"span">, "color">;

export const IconTailwind = ({
  size,
  fixedWidth,
  className,
  children,
  ...rest
}: TypeIconTailwindProps) => {
  // "Icon" preserves downstream descendant selectors (e.g. `.Icon` in
  // seeds-react-switch / seeds-react-data-viz) that the styled-components
  // Container added via .attrs({ className: "Icon" }).
  const classes = cn(
    "Icon",
    "seeds-icon",
    { "seeds-icon-fixed-width": fixedWidth },
    className
  );

  return (
    <span
      {...rest}
      className={classes}
      style={
        {
          ...rest.style,
          "--icon-size": sizeMap[size],
        } as React.CSSProperties
      }
    >
      {children}
    </span>
  );
};

IconTailwind.displayName = "IconTailwind";
